import { z } from 'zod';
export const ssoDomainsGuard = z.array(z.string());
export const ssoBrandingGuard = z.object({
    displayName: z.string().optional(),
    logo: z.string().optional(),
    darkLogo: z.string().optional(),
});
/**
 * Claim-based organization provisioning strategies. See the resolver module
 * at `packages/core/src/libraries/sso-organization-resolver.ts` for runtime behavior.
 *
 * - `id`: claim value is an existing organization ID; no auto-create.
 * - `path`: claim value is a hierarchy path (e.g. `/emea/tr/istanbul`). Segments are
 *   looked up case-insensitively under `rootOrganizationId` and optionally created.
 * - `groups`: claim value is an array of path strings; each element is processed
 *   like a `path` entry and memberships are merged.
 * - `attribute`: multiple claim values are joined into a path (e.g. department + ou)
 *   and processed like `path`.
 */
export const organizationProvisioningStrategyGuard = z.enum([
    'id',
    'path',
    'groups',
    'attribute',
]);
export const organizationProvisioningRuleGuard = z
    .object({
    strategy: organizationProvisioningStrategyGuard,
    /**
     * Claim/attribute name to read from the IdP payload. For the `attribute` strategy
     * this may be an array of attribute names whose non-empty values are joined into
     * a hierarchy path in the given order.
     */
    claimName: z.union([z.string().min(1), z.array(z.string().min(1)).min(1)]),
    /**
     * Separator used to split a single string claim into hierarchy segments.
     * Defaults to `/`. Ignored by the `id` and `attribute` strategies.
     */
    separator: z.string().min(1).optional(),
    /**
     * When true, missing segments are created under `rootOrganizationId`. When false,
     * unknown paths are silently skipped. Ignored by the `id` strategy (which never
     * auto-creates because an opaque ID cannot yield a sensible name).
     */
    autoCreate: z.boolean().optional(),
    /**
     * REQUIRED when `autoCreate` is true. Only descendants of this root may be
     * created. Prevents random organizations from appearing at the top level.
     */
    rootOrganizationId: z.string().min(1).optional(),
    /**
     * Optional map from resolved claim value (or the last path segment) to an
     * organization role ID. When a match is found, it overrides the org's default
     * JIT role for this user. Unmatched values fall back to `organization_jit_roles`.
     */
    roleMapping: z.record(z.string().min(1)).optional(),
})
    .refine((rule) => !rule.autoCreate || Boolean(rule.rootOrganizationId), {
    message: 'rootOrganizationId is required when autoCreate is true',
    path: ['rootOrganizationId'],
});
export const organizationProvisioningGuard = z.object({
    rules: z.array(organizationProvisioningRuleGuard).min(1),
});
